import React from 'react'
import cn from 'classnames'
import prefetchDetectedMarket from 'graphql/prefetchers/prefetchDetectedMarket'
import { searchClient } from 'lib/algolia'
import { colorsSecondary200 } from 'lib/theme'
import { trackCmsPageViewed } from 'lib/tracking/avo/trackPageView'
import { NextPage } from 'next'
import { NextSeo } from 'next-seo'
import { Configure, InstantSearch } from 'react-instantsearch-dom'

import CmsPageBasic from 'components/CmsPage/CmsPageBasic'
import ConnectedBreadcrumb from 'components/ConnectedBreadcrumb/ConnectedBreadcrumb'
import PublicLayout from 'components/PublicLayout/PublicLayout'
import SearchHeader from 'components/SearchHeader/SearchHeader'
import SearchResults from 'components/SearchResults/SearchResults'
import SearchSeo from 'components/SearchSeo/SearchSeo'
import WithFilter from 'components/UI/WithFilter/WithFilter'
import Wrapper from 'components/Wrapper/Wrapper'
import useAlgoliaIndices from 'hooks/useAlgoliaIndices'
import useEffectOnce from 'hooks/useEffectOnce'
import useRouter from 'hooks/useRouter'
import useSyncURLWithSearch from 'hooks/useSyncURLWithSearch'

import styles from './SearchPage.module.css'

const DEFAULT_FILTERS =
  'profileAcceptingOrders:true AND profileProductAcceptingOrders:true'

const SearchPage: NextPage = (): JSX.Element => {
  const router = useRouter()
  const { profilesIndex } = useAlgoliaIndices()
  const { searchState, syncURL, areFiltersApplied } = useSyncURLWithSearch()

  useEffectOnce(() => {
    trackCmsPageViewed({ pageSlug: 'search', path: router })
  })

  const userIsSearching = searchState.query || areFiltersApplied

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={profilesIndex}
      searchState={searchState}
      onSearchStateChange={syncURL}
    >
      {userIsSearching && <SearchSeo />}
      <Configure clickAnalytics filters={DEFAULT_FILTERS} />
      <PublicLayout
        headerBackground={colorsSecondary200}
        headerBorder
        hidePress
        headerContent={<SearchHeader />}
        navigationBarDarkContent
      >
        <NextSeo noindex />

        <Wrapper edgeToEdge>
          <div className={styles.breadcrumbWrapper}>
            <ConnectedBreadcrumb
              attributes={[
                'publicFilters.categories.lvl0',
                'publicFilters.categories.lvl1',
                'publicFilters.categories.lvl2',
              ]}
            />
          </div>

          <WithFilter>
            <div
              className={cn({
                [styles.hide]: !userIsSearching,
              })}
            >
              <SearchResults searchTerm={searchState.query} />
            </div>

            {!userIsSearching && (
              <div className={styles.cmsPageWrapper}>
                <CmsPageBasic slug="search" />
              </div>
            )}
          </WithFilter>
        </Wrapper>
      </PublicLayout>
    </InstantSearch>
  )
}

SearchPage.getInitialProps = prefetchDetectedMarket

export default SearchPage
